import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '@/utils/api';
import { QualityEvidenceFiles } from './QualityEvidenceFiles';
import { evaluateSample, type IqcParam } from './iqcBands';

interface LotRow { id: string; lotNo: string; grnNumber: string; productName: string; vendorName: string; qty: number; uom: string; checks: { iqc: string } }
interface Sheet {
  lot: { id: string; lotNo: string; productName: string; vendorName: string; qty: number; uom: string; status: string };
  template: { id: string; name: string; sampleSize: number } | null;
  params: IqcParam[];
  latest: { number: string; recordedByName: string; readings: Array<{key:string;samples:Array<string|number|boolean|null>;instrumentCode:string|null}> } | null;
  formatMissingMessage: string | null;
}
type Row = { samples: string[]; instrument: string };

const CARD = 'rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5';
const INPUT = 'w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-2 py-1.5 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none';

export function IqcReadingsSheet({ lotId, onSubmitted }: { lotId?: string; onSubmitted?: () => void } = {}) {
  const [lots, setLots] = useState<LotRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const loadLots = useCallback(async () => {
    setLoading(true);setLoadError('');
    try { const res = await api.get('/quality/lots', { params: { status: 'quarantine' } });
      if (!Array.isArray(res.data.data)) throw new Error('Invalid lot list');
      setLots(res.data.data.filter((l: LotRow) => l.checks?.iqc !== 'not_required'));
    } catch { setLoadError('Could not load assigned lots.'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { void loadLots(); }, [loadLots]);

  const open = async (lotId: string) => {
    setSelected(lotId); setSheet(null); setMessage(null); setPhotos([]);
    try { const res = await api.get(`/quality/iqc/lots/${lotId}/sheet`);
    const s: Sheet = res.data.data;
    setSheet(s);
    const size = s.template?.sampleSize ?? 1;
    setRows(Object.fromEntries(s.params.map((p) => [p.key, { samples: s.latest?.readings?.find(r=>r.key===p.key)?.samples.map(v=>String(v??'')) ?? Array(size).fill(''), instrument: s.latest?.readings?.find(r=>r.key===p.key)?.instrumentCode ?? '' }])));
    } catch { setSelected(null); setLoadError('Could not open the assigned inspection. Reload your queue.'); }
  };

  useEffect(() => { if (lotId) void open(lotId); }, [lotId]);

  const setSample = (key: string, i: number, v: string) =>
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], samples: prev[key].samples.map((x, j) => (j === i ? v : x)) } }));

  const submit = async () => {
    if (!sheet || !selected) return;
    setBusy(true); setMessage(null);
    try {
      const res = await api.post(`/quality/iqc/lots/${selected}/readings`, {
        readings: sheet.params.map((p) => ({ key: p.key, samples: rows[p.key].samples, instrumentCode: rows[p.key].instrument.trim() || null })),
        photos,
      });
      setMessage({ kind: 'ok', text: `${res.data.data.number} sent to the QA manager.` });
      void loadLots(); onSubmitted?.();
    } catch (err: any) {
      setMessage({ kind: 'error', text: err.response?.data?.error || 'Readings were not saved.' });
    } finally { setBusy(false); }
  };

  const size = sheet?.template?.sampleSize ?? 0;

  return (
    <div className={CARD}>
      <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
        <ClipboardCheck size={16} className="text-[var(--ink-0)]" />
        Incoming material — IQC readings
        <span className="ml-auto text-xs text-[var(--text-muted)]">{lots.length.toLocaleString('en-IN')} lots waiting</span>
      </h2>
      {loading && <p role="status">Loading lots…</p>}{loadError && <p role="alert">{loadError} <button onClick={loadLots}>Retry</button></p>}
      <div className="mb-4 flex flex-wrap gap-2">
        {!loading && !loadError && lots.length === 0 && <p className="text-sm text-[var(--text-muted)]">No lots waiting for readings</p>}
        {!lotId && !loading && !loadError && lots.map((l) => (
          <button key={l.id} onClick={() => open(l.id)}
            className={`rounded-lg border px-3 py-1.5 text-xs ${selected === l.id ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}>
            {l.grnNumber} · {l.lotNo} · {l.productName}
          </button>
        ))}
      </div>
      {selected && !sheet && <Loader2 size={16} className="animate-spin text-[var(--text-muted)]" />}
      {sheet && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
            <div><span className="text-[var(--text-muted)]">GRN lot </span>{sheet.lot.lotNo}</div>
            <div><span className="text-[var(--text-muted)]">Material </span>{sheet.lot.productName}</div>
            <div><span className="text-[var(--text-muted)]">Format </span>{sheet.template ? `${sheet.template.name} · sample ${sheet.template.sampleSize}` : '—'}</div>
          </div>
          {sheet.formatMissingMessage ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertTriangle size={14} /> {sheet.formatMissingMessage}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="pb-2 pr-3">Parameter</th>
                      <th className="pb-2 pr-3">Spec</th>
                      {Array.from({ length: size }, (_, i) => <th key={i} className="pb-2 pr-2">{`S${i + 1}`}</th>)}
                      <th className="pb-2 pr-3">Instrument</th>
                      <th className="pb-2">Band</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.params.map((p) => {
                      const r = rows[p.key];
                      const verdicts = r.samples.map((s) => evaluateSample(p, s));
                      const band = verdicts.includes('out') ? 'out' : verdicts.every((v) => v === 'in') ? 'in' : '—';
                      return (
                        <tr key={p.key} className="border-t border-[var(--border-primary)]/50">
                          <td className="py-2 pr-3 font-medium text-[var(--text-primary)]">{p.label}</td>
                          <td className="py-2 pr-3 text-xs text-[var(--text-muted)]">{p.specText}</td>
                          {r.samples.map((s, i) => (
                            <td key={i} className="py-2 pr-2">
                              <input aria-label={`${p.label} S${i + 1}`} value={s} onChange={(e) => setSample(p.key, i, e.target.value)}
                                className={`${INPUT} ${verdicts[i] === 'out' ? 'border-red-400 text-red-600' : ''}`} />
                            </td>
                          ))}
                          <td className="py-2 pr-3">
                            <input aria-label={`${p.label} instrument`} value={r.instrument} placeholder={p.type === 'number' ? 'TG-02' : 'visual'}
                              onChange={(e) => setRows((prev) => ({ ...prev, [p.key]: { ...prev[p.key], instrument: e.target.value } }))} className={INPUT} />
                          </td>
                          <td className="py-2">
                            <span data-testid={`band-${p.key}`} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${band === 'out' ? 'bg-red-50 text-red-700' : band === 'in' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{band}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <QualityEvidenceFiles files={photos} onChange={setPhotos} entityId={selected ?? undefined} />
              <div className="flex items-center justify-end gap-3">
                {message && (
                  <span role={message.kind === 'error' ? 'alert' : 'status'} className={`flex items-center gap-1 text-sm ${message.kind === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                    {message.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {message.text}
                  </span>
                )}
                <button onClick={submit} disabled={busy || sheet.lot.status !== 'quarantine'}
                  className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                  {busy && <Loader2 size={14} className="animate-spin" />}Submit to QA Manager
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
